import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Box } from '@chakra-ui/react';
import AppleLogo from 'Shared/icons/logos/apple.svg';
import { isAndroidMobileApp, loadAppleSdk } from 'Shared/appleHelper';
import { _js } from '@ifixit/localize';

export function AppleSSOButton({
   loginHandler,
   setError,
   text,
}: {
   loginHandler: (response: any) => void;
   setError: (error: string) => void;
   text: string;
}) {
   const [isLoading, setIsLoading] = useState(false);
   const [isReady, setIsReady] = useState(false);
   const userAgent = navigator.userAgent || '';
   const onAndroidMobileApp = useMemo(() => isAndroidMobileApp(userAgent), [userAgent]);

   useEffect(() => {
      if (!App.appleLoginEnabled || !App.appleClientId) {
         return;
      }

      loadAppleSdk(() => setIsReady(true));
   }, []);

   const handleClick = useCallback(async () => {
      if (!isReady || isLoading) return;

      setIsLoading(true);
      try {
         const response = await window.AppleID.auth.signIn();

         // Send the identity token to our backend
         const apiResponse = await fetch('/api/2.0/user/login/appleLogin', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               identityToken: response.authorization.id_token,
               user: response.user,
            }),
         });

         const result = await apiResponse.json();

         if (result.loggedIn) {
            result.from = 'apple';
            loginHandler(result);
         } else {
            setError(result.reason || _js('Unable to complete Apple login.'));
         }
      } catch (error: any) {
         // User cancelled or other error
         if (error?.error !== 'popup_closed_by_user') {
            setError(_js('Unable to complete Apple login.'));
         }
      } finally {
         setIsLoading(false);
      }
   }, [isReady, isLoading, loginHandler, setError]);

   if (!App.appleLoginEnabled || onAndroidMobileApp) {
      return null;
   }

   return (
      <Button
         id="appleLogin"
         fontWeight="medium"
         minHeight="40px"
         width="100%"
         px={0}
         onClick={handleClick}
         isLoading={isLoading}
         isDisabled={!isReady}
         data-mobile-appid="appleLogin"
         bg="white"
         color="black"
         _hover={{ bg: 'gray.100' }}
      >
         <AppleLogo />
         <Box as="span" marginLeft={2}>
            {text}
         </Box>
      </Button>
   );
}
